import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import useFilterStore from 'zustandStore/useFilterStore';
import RpRangeAutocomplete from 'components/commons/RpRangeAutocomplete';
import SearchFilterUtil from 'lib/SearchFilterUtil';
import { RENTAL_PRICE_OPTIONS_MIN, RENTAL_PRICE_OPTIONS_MAX } from 'constants/filterOptions';
import { validatePrice } from 'lib/Validations';
import { FREQUENCY_OPTIONS, FREQUENCIES } from 'constants/filterOptions';
import Colors from 'constants/colors';

const RENTAL_FIELD = 'rentals';

const RentalPriceFilter = React.memo(function RentalPriceFilter(props) {
    const filter = useFilterStore(state => state.filters[RENTAL_FIELD]?.price);
    const priceFrequency = useFilterStore(state => state.filters[RENTAL_FIELD]?.rentalPriceFrequency);
    const setFilters = useFilterStore(state => state.setFilters);
    const errors = useFilterStore(state => state.errors[RENTAL_FIELD]);
    const setError = useFilterStore(state => state.setError);
    const removeError = useFilterStore(state => state.removeError);

    const [selectedFrequency, setSelectedFrequency] = useState(priceFrequency || FREQUENCIES.week);

    const validateInput = (input) => {
        return validatePrice(SearchFilterUtil.getNumericValue(SearchFilterUtil.expandAndFormatPrice(input)));
    };

    const minValue = filter?.min
        ? SearchFilterUtil.findOptionByValue(RENTAL_PRICE_OPTIONS_MIN, filter.min)
        : { value: '', label: 'Min' };
    const maxValue = filter?.max
        ? SearchFilterUtil.findOptionByValue(RENTAL_PRICE_OPTIONS_MAX, filter.max)
        : { value: '', label: 'Max' };

    const onError = (error) => {
        if (Boolean(error.length)) {
            setError(RENTAL_FIELD, 'price');
        } else {
            removeError(RENTAL_FIELD, 'price');
        }
    };

    const handleFrequencyChange = (event, newValue) => {
        const value = FREQUENCY_OPTIONS[newValue];
        setSelectedFrequency(value);
        if ((filter.min || filter.max) && priceFrequency !== value) {
            setFilters(RENTAL_FIELD, { rentalPriceFrequency: value });
        }
    };

    const onChange = ({ min, max }) => {
        setFilters(RENTAL_FIELD, { price: { min: min.value, max: max.value }, rentalPriceFrequency: selectedFrequency });
    };

    return (
        <RpRangeAutocomplete
            label="Rental Price"
            active={minValue.value !== '' || maxValue.value !== ''}
            error={errors.includes('price')}
            subLabel={props.subLabel}
            minOptions={RENTAL_PRICE_OPTIONS_MIN}
            maxOptions={RENTAL_PRICE_OPTIONS_MAX}
            minValue={minValue}
            maxValue={maxValue}
            formatter={SearchFilterUtil.expandAndFormatPrice}
            onChange={onChange}
            validateInput={validateInput}
            dropdownDisplayFormatter={SearchFilterUtil.getDisplayPriceValue}
            isDropdown={props.isDropdown}
            onError={onError}
            value={SearchFilterUtil.formatPriceRangeDisplay(minValue.value, maxValue.value)}
            maxInputLength={14}
            showValueAsTooltip
        >
            <Tabs
                value={FREQUENCY_OPTIONS.indexOf(selectedFrequency)}
                onChange={handleFrequencyChange}
                aria-label="Frequency Tabs"
                sx={{
                    backgroundColor: '#fafafa',
                    mb: 2,
                    mt: 2,
                    minHeight: '32px',
                    border: props.outlined ? '1px solid #E0E0E0' : 'none',
                    borderRadius: props.outlined ? '4px' : 0,
                    '&  .MuiTabs-indicator': {
                        backgroundColor: props.outlined ? 'transparent' : '#1890ff',
                    },
                }}
            >
                {FREQUENCY_OPTIONS.map((freq) => (
                    <Tab
                        key={freq}
                        label={freq}
                        sx={{
                            fontSize: '13px',
                            textTransform: 'capitalize',
                            lineHeight: '22px',
                            fontWeight: 500,
                            padding: '5px',
                            letterSpacing: '0.46px',
                            color: Colors.BLACK_30_OPAQUE,
                            minHeight: '32px',
                            minWidth: '33.33%',
                            '&:not(:last-child)': {
                                borderRight: props.outlined ? '1px solid #E0E0E0' : 'none',
                            },
                            '&.Mui-selected': {
                                color: props.outlined ? Colors.PRIMARY.MAIN : Colors.BLACK_87_OPAQUE,
                            },
                        }}
                    />
                ))}
            </Tabs>
        </RpRangeAutocomplete>
    );
});

RentalPriceFilter.propTypes = {
    isDropdown: PropTypes.bool,
    outlined: PropTypes.bool,
};

export default RentalPriceFilter;
